<template>
  <div>
    <v-dialog v-model="deleteDialog" max-width="300px">
      <div class="text-center">
        <v-sheet
          class="px-7 pt-7 pb-4 mx-auto text-center d-inline-block"
          color="blue-grey darken-3"
          dark
        >
          <div class="grey--text text--lighten-1 text-body-2 mb-4">
            {{
              $t(
                'Tem certeza de que deseja excluir este template de checklist?'
              )
            }}
          </div>

          <v-btn
            :disabled="loading"
            class="ma-1"
            color="grey"
            text
            @click="deleteDialog = false"
          >
            Cancelar
          </v-btn>

          <v-btn
            :loading="loading"
            class="ma-1"
            color="error"
            text
            @click="remove"
          >
            {{ $t('Excluir') }}
          </v-btn>
        </v-sheet>
      </div>
    </v-dialog>

    <v-dialog v-model="loadModal" max-width="600px" @click:outside="closeLoadModal">
      <v-card class="card-pad mx-auto" width="600">
        <div class="pa-4">
          <!-- Step 1: select department types (only when institution has both configured) -->
          <template v-if="!previewData && institutionDepartmentTypes.length > 1">
            <h3 class="text-center mb-3">
              {{ $t('Selecione os equipamentos que deseja importar.') }}
            </h3>
            <div class="d-flex justify-center px-4">
              <v-checkbox
                class="pr-3"
                v-model="departmentTypes"
                label="Engenharia clínica"
                :value="0"
              ></v-checkbox>
              <v-checkbox
                v-model="departmentTypes"
                label="Manutenção predial"
                :value="1"
              ></v-checkbox>
            </div>
            <div class="d-flex justify-center px-4 mt-2">
              <v-btn class="ma-1" color="grey" text @click="closeLoadModal">
                {{ $t('Cancelar') }}
              </v-btn>
              <v-btn
                class="ma-1"
                color="secondary"
                text
                :loading="previewLoading"
                :disabled="departmentTypes.length === 0"
                @click="getLoadPreview"
              >
                {{ $t('Ver preview') }}
              </v-btn>
            </div>
          </template>


          <!-- Step 2: preview results -->
          <template v-else-if="previewData">
            <h3 class="text-center mb-4">{{ $t('Preview da importação') }}</h3>

            <div v-if="previewData.toImport.length > 0" class="mb-2">
              <div
                class="d-flex align-center mb-1 cursor-pointer"
                @click="previewExpanded.toImport = !previewExpanded.toImport"
              >
                <v-icon color="success" small class="mr-1">mdi-check-circle</v-icon>
                <strong>{{ $t('Serão importados') }} ({{ previewData.toImport.length }})</strong>
                <v-spacer></v-spacer>
                <v-icon small>{{ previewExpanded.toImport ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
              </div>
              <v-list v-if="previewExpanded.toImport" dense class="pa-0">
                <v-list-item
                  v-for="item in previewData.toImport"
                  :key="'import-' + item.id"
                  class="px-2"
                >
                  <v-list-item-content>
                    <v-list-item-title class="text-body-2">{{ item.name }}</v-list-item-title>
                    <v-list-item-subtitle v-if="item.equipments.length > 0" class="text-caption">
                      {{ item.equipments.join(', ') }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </div>

            <div v-if="previewData.cannotImport.length > 0" class="mb-2">
              <div
                class="d-flex align-center mb-1 cursor-pointer"
                @click="previewExpanded.cannotImport = !previewExpanded.cannotImport"
              >
                <v-icon color="error" small class="mr-1">mdi-alert-circle</v-icon>
                <strong>{{ $t('Não serão importados — equipamento padrão ausente') }} ({{ previewData.cannotImport.length }})</strong>
                <v-spacer></v-spacer>
                <v-icon small>{{ previewExpanded.cannotImport ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
              </div>
              <v-list v-if="previewExpanded.cannotImport" dense class="pa-0">
                <v-list-item
                  v-for="item in previewData.cannotImport"
                  :key="'cannot-' + item.id"
                  class="px-2"
                >
                  <v-list-item-content>
                    <v-list-item-title class="text-body-2">{{ item.name }}</v-list-item-title>
                    <v-list-item-subtitle class="text-caption error--text">
                      {{ $t('Faltando') }}: {{ item.missingEquipments.join(', ') }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </div>

            <div v-if="previewData.alreadyImported.length > 0" class="mb-2">
              <div
                class="d-flex align-center mb-1 cursor-pointer"
                @click="previewExpanded.alreadyImported = !previewExpanded.alreadyImported"
              >
                <v-icon color="grey" small class="mr-1">mdi-minus-circle</v-icon>
                <strong>{{ $t('Já importados') }} ({{ previewData.alreadyImported.length }})</strong>
                <v-spacer></v-spacer>
                <v-icon small>{{ previewExpanded.alreadyImported ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
              </div>
              <v-list v-if="previewExpanded.alreadyImported" dense class="pa-0">
                <v-list-item
                  v-for="item in previewData.alreadyImported"
                  :key="'already-' + item.id"
                  class="px-2"
                >
                  <v-list-item-content>
                    <v-list-item-title class="text-body-2 grey--text">{{ item.name }}</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </div>

            <div class="d-flex justify-space-between mt-3">
              <v-btn
                depressed
                :disabled="loading"
                class="ma-1 secondary-button"
                style="color: white; background-color: #b14949 !important"
                @click="institutionDepartmentTypes.length > 1 ? previewData = null : closeLoadModal()"
              >
                {{ institutionDepartmentTypes.length > 1 ? $t('Voltar') : $t('Cancelar') }}
              </v-btn>
              <v-btn
                v-if="previewData.toImport.length > 0"
                depressed
                class="ma-1 primary-button"
                style="color: white; background-color: #0f3582 !important"
                :loading="loading"
                @click="loadChecklistTemplates"
              >
                {{ $t('Confirmar importação') }}
              </v-btn>
            </div>
          </template>
        </div>
      </v-card>
    </v-dialog>

    <div class="hidden-sm-and-down mb-2">
      <div class="d-flex">
        <v-spacer></v-spacer>
        <v-btn
          dark
          class="secondary-button mr-0"
          @click="addChecklistTemplate"
          depressed
        >
          {{ $t('Novo Template') }}
        </v-btn>
        <v-btn
          dark
          class="mx-2 terciary-button"
          @click="openLoadModal"
          depressed
        >
          {{ $t('Carregar templates padrão') }}
        </v-btn>
        <v-btn
          class="primary-button"
          dark
          depressed
          @click="visible = !visible"
        >
          Filtros
        </v-btn>
      </div>
    </div>

    <div class="my-5 back hidden-md-and-up">
      <v-spacer></v-spacer>
      <v-btn
        small
        class="mr-2"
        color="#0F3565"
        depressed
        dark
        @click="addChecklistTemplate"
      >
        <v-icon>mdi-plus</v-icon>
      </v-btn>

      <v-btn small color="#BBCF20" depressed dark @click="visible = !visible">
        <v-icon> mdi-filter </v-icon>
      </v-btn>
    </div>

    <v-expand-transition>
      <div v-show="visible">
        <v-row class="ma-0" dense>
          <v-col class="py-0" cols="12" sm="6" md="6" lg="6">
            <label class="label">Template</label>
            <v-text-field
              hide-details="auto"
              v-model="checklistTemplateName"
              outlined
              class="primary-input"
              @keyup.enter="filterTable"
            ></v-text-field>
          </v-col>
          <v-col class="py-0" cols="12" sm="6" md="6" lg="6">
            <label class="label">Item checklist</label>
            <v-text-field
              hide-details="auto"
              name="nameItem"
              v-model="checklistItemName"
              outlined
              class="primary-input"
              @keyup.enter="filterTable"
            ></v-text-field>
          </v-col>
          <v-col class="py-0" cols="12" sm="6" md="6" lg="6">
            <label class="label">{{ $t('Classe') }}</label>
            <v-autocomplete
              hide-details="auto"
              v-model="checklistItemClassId"
              :items="checklistItemClasses"
              item-value="id"
              item-text="name"
              :loading="classLoading"
              outlined
              class="primary-input"
              @keyup.enter="filterTable"
            ></v-autocomplete>
          </v-col>
          <v-col class="py-0" cols="12" sm="6" md="6" lg="6">
            <label class="label">{{ $t('Equipamento padrão') }}</label>
            <v-autocomplete
              hide-details="auto"
              v-model="standardEquipmentsSelected"
              :items="standardEquipments"
              item-text="name"
              :search-input.sync="search"
              @blur="search = ''"
              @keyup.enter="filterTable"
              clearable
              chips
              small-chips
              multiple
              deletable-chips
              return-object
              outlined
              class="primary-input"
            >
              <template v-slot:selection="{ attrs, item, select, selected }">
                <v-chip
                  v-bind="attrs"
                  :input-value="selected"
                  close
                  color="#BBCF20"
                  text-color="#fff"
                  small
                  @click="select"
                  @click:close="removeStandardEquipment(item)"
                >
                  {{ item.name }}
                </v-chip>
              </template>
              <template v-slot:item="{ item }">
                <span class="text-caption">
                  {{ item.name }}
                </span>
              </template>
            </v-autocomplete>
          </v-col>
          <v-col cols="12" class="d-flex align-center justify-center">
            <v-btn
              class="mx-2 secondary-button"
              color="#BBCF20"
              @click="filterTable"
              depressed
            >
              <span>{{ $t('Filtrar') }}</span>
              <v-icon class="pl-2">mdi-magnify</v-icon>
            </v-btn>

            <v-btn
              depressed
              color="#20202066"
              class="clear-button"
              @click="reset"
            >
              <span>{{ $t('Limpar') }}</span>
              <v-icon class="pl-2">mdi-cached</v-icon>
            </v-btn>
          </v-col>
        </v-row>
      </div>
    </v-expand-transition>

    <div>
      <v-data-table
        :loading="loading"
        :loading-text="$t('Carregando...')"
        :no-data-text="$t('Nenhum template de checklist encontrado')"
        :headers="headers"
        :items="checklistTemplates"
        class="main-table"
        hide-default-footer
        :items-per-page="pageSize"
        :header-props="headerProps"
        :mobile-breakpoint="330"
        :single-expand="true"
        show-expand
        hide-default-header
        :expanded.sync="expanded"
        @click:row="(item, slot) => slot.expand(!slot.isExpanded)"
      >
        <template #header="{ props, on }">
          <thead>
            <tr>
              <th
                class="text-center"
                v-for="header in props.headers"
                :key="header.value"
              >
                <v-simple-checkbox
                  v-if="header.value === 'data-table-select'"
                  v-model="props.everyItem"
                  :indeterminate="props.someItems && !props.everyItem"
                  color="purple"
                  @input="on['toggle-select-all']"
                ></v-simple-checkbox>

                <div>
                  <v-hover v-slot="{ hover }">
                    <div>
                      <span>{{ $t(header.text) }}</span>
                      <v-icon
                        v-if="
                          hover &&
                          header.value != 'data-table-select' &&
                          header.value != 'actions'
                        "
                        color="white"
                        class="mx-2"
                        @click="on.sort(header.value)"
                      >
                        {{
                          props.options.sortBy.includes(header.value)
                            ? `mdi-chevron-down`
                            : `mdi-chevron-up`
                        }}
                      </v-icon>
                    </div>
                  </v-hover>
                </div>
              </th>
            </tr>
          </thead>
        </template>

        <template #[`item.name`]="{ item }">
          {{ item.name }}
        </template>

        <template #[`item.actions`]="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                class="ma-1"
                color="#0F3565"
                dark
                @click="editChecklistTemplate(item)"
                v-bind="attrs"
                v-on="on"
                small
                depressed
              >
                <v-icon small dark> mdi-pencil-outline </v-icon>
              </v-btn>
            </template>
            <span>{{ $t('Editar') }}</span>
          </v-tooltip>

          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                v-if="(userType >= 0 && userType <= 2) || userType === 8"
                class="ma-1"
                small
                color="#A42A2AD9"
                style="font-weight: bold"
                dark
                v-bind="attrs"
                @click="deleteChecklistTemplate(item)"
                v-on="on"
                depressed
              >
                <v-icon small color="white"> mdi-trash-can-outline </v-icon>
              </v-btn>
            </template>
            <span>{{ $t('Excluir') }}</span>
          </v-tooltip>

          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                class="ma-1"
                color="#0F3565"
                dark
                @click="getPrint(item.id)"
                v-bind="attrs"
                v-on="on"
                small
                depressed
              >
                <v-icon small dark> mdi-printer </v-icon>
              </v-btn>
            </template>
            <span>{{ $t('Imprimir') }}</span>
          </v-tooltip>

          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                class="ma-1"
                color="#BBCF20"
                dark
                @click="openOrdenationDialog(item)"
                v-bind="attrs"
                v-on="on"
                small
                depressed
              >
                <v-icon small dark>mdi-sort</v-icon>
              </v-btn>
            </template>
            <span>Ordenar Itens</span>
          </v-tooltip>
        </template>

        <template v-slot:expanded-item="{ headers, item }">
          <td :colspan="headers.length">
            <v-col class="ma-0 pa-0" cols="12">
              <strong>{{ $t('Equipamentos padrão') }}:</strong>
              <span
                v-for="(equip, index) in item.templateStandardEquipment"
                :key="equip.id"
              >
                {{
                  index == item.templateStandardEquipment.length - 1
                    ? equip.standardEquipment.name
                    : `${equip.standardEquipment.name} -`
                }}
              </span>
            </v-col>
          </td>
        </template>
      </v-data-table>

      <checklistTemplateOrdenation
        v-if="ordenationDialog"
        :value="ordenationDialog"
        :template="selectedTemplate"
        :items="selectedTemplateItems"
        :checklistItemClasses="checklistItemClasses"
        @input="ordenationDialog = $event"
        @save="selectedTemplateItems = $event"
      />

      <div class="pagination">
        <div class="item-pagination block-one">
          <v-menu bottom offset-y>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                v-bind="attrs"
                v-on="on"
                class="btn"
                depressed
                color="#C0C0C0"
                small
              >
                qtd: {{ pageSize }}
              </v-btn>
            </template>
            <v-list>
              <v-list-item
                v-for="(size, i) in sizes"
                :key="i"
                @click="pageSize = size"
              >
                <v-list-item-title>{{ size }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>

          <div class="registros" v-if="totalItensOnPage">
            <span> 1 - {{ totalItensOnPage }} de {{ totalItems }} </span>
          </div>
        </div>

        <div class="item-pagination block-two">
          <v-pagination
            class="pagination-item"
            v-model="page"
            :length="totalPages"
            :total-visible="9"
          ></v-pagination>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@import '@/sass/variables.scss';
.mobile {
  display: none;
  @media (max-width: $tablet-1) {
    display: block;
  }
}
.desktop {
  @media (max-width: $tablet-1) {
    display: none;
  }
}
.v-application .primary--text {
  color: #707070 !important;
  caret-color: #707070 !important;
}

.row-pointer >>> tbody tr :hover {
  cursor: pointer;
}
.cursor-pointer {
  cursor: pointer;
  user-select: none;
}
</style>

<script>
import ApiService from '../../../../../services/ApiService'
import GeneralService from '../../../../../services/GeneralService'
import checklistTemplateOrdenation from './checklistTemplateOrdenation.vue'

export default {
  async created() {
    try {
      const institution = JSON.parse(localStorage.getItem('institution'))
      this.userType = institution.userType
      this.institutionId = institution.id
      this.institutionDepartmentTypes = institution.departmentTypes || []
    } catch (e) {
      this.$toast.error(this.$t('Erro ao carregar dados da instituição.'))
      return
    }
    this.getChecklisItemClasses()
    this.getStandardEquipments()
    this.updateTable()
  },

  data: () => ({
    ordenationDialog: false,
    selectedTemplate: null,

    selectedTemplateItems: [],

    loadModal: false,
    totalItensOnPage: 0,
    visible: false,
    dialog: false,
    editDialog: false,
    deleteDialog: false,
    dialogKey: false,
    loading: false,
    classLoading: false,
    standardEquipmentLoading: false,
    error: false,
    expanded: [],
    checklistItemClassId: null,
    checklistItemClasses: [],
    standardEquipmentId: null,
    standardEquipments: [],
    standardEquipmentsSelected: [],
    checklistTemplates: [],
    currentChecklistTemplate: {},
    templateStandardEquipment: [],
    userType: null,
    institutionId: null,
    search: null,
    page: 1,
    totalPages: 1,
    pageSize: 10,
    sizes: [10, 30, 90, 180],
    totalItems: 0,
    filter: {
      page: 1,
      pageSize: 10,
      name: null,
      classId: null,
      checklistItemName: null,
      standardEquipments: [],
      institutionId: null,
    },
    checklistTemplateName: null,
    checklistItemName: null,
    headers: [
      {
        text: 'Template',
        align: 'center',
        value: 'name',
      },
      {
        text: 'Ações',
        align: 'center',
        value: 'actions',
        sortable: false,
      },
    ],
    headerProps: {
      sortByText: 'Ordenar por',
    },
    apiService: new ApiService(),
    generalService: new GeneralService(),
    departmentTypes: [],
    institutionDepartmentTypes: [],
    previewData: null,
    previewLoading: false,
    previewExpanded: { toImport: true, cannotImport: true, alreadyImported: false },
  }),

  components: {
    checklistTemplateOrdenation,
  },

  methods: {
    async openOrdenationDialog(template) {
      // Busca os itens do template via API para garantir dados completos e atualizados
      this.selectedTemplate = template
      this.selectedTemplateItems = []

      try {
        // O ManageTemplate usa response.content.templateItems.map(x => x.checklistItem)
        const response = await this.apiService.get(
          `checklistTemplate/${template.id}`
        )

        this.selectedTemplateItems = response.content.templateItems || []
      } catch (e) {
        // fallback seguro
        this.selectedTemplateItems = []
      }

      this.ordenationDialog = true
    },

    getPrint(id) {
      this.apiService.get(`checklistTemplate/print/${id}`).then((res) => {
        var pdfResult = `data:application/pdf;base64,${res.content}`
        const link = document.createElement('a')
        link.href = pdfResult
        link.setAttribute('download', `Checklist.pdf`)
        document.body.appendChild(link)
        link.click()
      })
    },

    openLoadModal() {
      this.previewData = null

      if (this.institutionDepartmentTypes.length <= 1) {
        this.departmentTypes = [...this.institutionDepartmentTypes]
        this.getLoadPreview()
        return
      }

      this.loadModal = true
    },

    closeLoadModal() {
      this.loadModal = false
      this.previewData = null
      this.previewExpanded = { toImport: true, cannotImport: true, alreadyImported: false }
    },

    async getLoadPreview() {
      this.previewLoading = true

      const model = {
        institutionId: this.institutionId,
        departmentTypes: this.departmentTypes,
      }

      try {
        const response = await this.apiService.post(`standardChecklistTemplate/load/preview`, model)
        const data = response?.content

        if (!data || !Array.isArray(data.toImport)) {
          this.$toast.error(this.$t('Resposta inesperada do servidor.'))
          return
        }

        if (data.toImport.length === 0) {
          this.$toast.warning(this.$t('Não há templates padrão a importar para esta instituição.'))
          this.closeLoadModal()
          return
        }

        this.previewData = data
        this.loadModal = true
      } catch (e) {
        this.$toast.error(this.$t('Erro ao carregar preview de importação.'))
      } finally {
        this.previewLoading = false
      }
    },

    async loadChecklistTemplates() {
      this.loading = true

      const model = {
        institutionId: this.institutionId,
        departmentTypes: this.departmentTypes,
      }

      await this.apiService
        .post(`standardChecklistTemplate/load`, model)
        .then(() => {
          this.loading = false
          this.closeLoadModal()
          this.updateTable()
        })
        .catch((error) => {
          this.loading = false
          this.$toast.error(this.$t('Erro ao importar templates padrão.'))
          console.error('Erro ao importar templates padrão:', error)
        })
    },

    addChecklistTemplate() {
      this.$router.push('/newTemplate')
    },

    editChecklistTemplate(checklistTemplate) {
      this.$router.push(`/editTemplate/${checklistTemplate.id}`)
    },

    deleteChecklistTemplate(checklistTemplate) {
      this.currentChecklistTemplate = checklistTemplate
      this.dialogKey = !this.dialogKey
      this.deleteDialog = true
    },

    closeDialog() {
      this.dialog = false
      this.editDialog = false

      this.updateTable()
    },

    removeStandardEquipment(item) {
      this.standardEquipmentsSelected.splice(
        this.standardEquipmentsSelected.indexOf(item),
        1
      )
      this.standardEquipmentsSelected = [...this.standardEquipmentsSelected]
    },

    filterTable() {
      this.page = 1
      this.filter.page = this.page
      this.filter.pageSize = this.pageSize
      this.filter.name = this.checklistTemplateName
      this.filter.classId = this.checklistItemClassId
      this.filter.checklistItemName = this.checklistItemName
      this.filter.standardEquipments = this.standardEquipmentsSelected
      this.updateTable()
    },

    async updateTable() {
      this.filter.institutionId = this.institutionId
      this.loading = true
      await this.apiService
        .post('checklistTemplate/list', this.filter)
        .then((response) => {
          this.totalPages = response.content.pager.totalPages
          this.checklistTemplates = response.content.checklistTemplates
          this.totalItems = response.content.pager.totalItems
          this.totalItensOnPage = response.content.checklistTemplates.length

          this.templateStandardEquipment = this.checklistTemplates.map(
            (template) =>
              template.templateStandardEquipment.map(
                (equipment) => equipment.standardEquipment
              )
          )

          this.loading = false
        })
    },

    async remove() {
      this.loading = true

      await this.apiService
        .delete('checklistTemplate/delete', this.currentChecklistTemplate)
        .then(() => {
          this.loading = false
          this.deleteDialog = false
          this.updateTable()
        })
        .catch((error) => {
          this.loading = false
          this.deleteDialog = false
          this.$toast.error(this.$t('Erro ao excluir template de checklist.'))
          console.error('Erro ao excluir template de checklist:', error)
        })
    },

    async getChecklisItemClasses() {
      this.classLoading = true

      await this.apiService
        .get(`checklistItemClass/list/${this.institutionId}`)
        .then((resposne) => {
          this.checklistItemClasses = resposne.content
          this.classLoading = false
        })
    },

    async getStandardEquipments() {
      this.standardEquipmentLoading = true

      this.standardEquipments =
        await this.generalService.getStandardEquipmentsByInstitution(
          this.institutionId
        )

      this.standardEquipmentLoading = false
    },

    reset() {
      this.filter = {
        page: 1,
        pageSize: 10,
        name: null,
        classId: null,
        checklistItemName: null,
        standardEquipments: [],
      }
      this.checklistItemClassId = null
      this.checklistItemName = null
      this.checklistTemplateName = null
      this.standardEquipmentId = null
      this.standardEquipmentsSelected = []
      this.updateTable()
    },
  },

  watch: {
    pageSize() {
      this.filter.pageSize = this.pageSize
      this.updateTable()
    },

    page() {
      this.filter.page = this.page
      this.updateTable()
    },
  },
}
</script>
